import React, {useEffect, useState, useCallback} from "react";
import {Button, Modal, Form, Row, Col, notification, Card, Progress, Divider, Input} from "antd";
import { EyeOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AllPartnersForOrdersSelect from "../Partners/AllPartnersForOrdersSelect";
import AvailableLocationsSelect from "../Partners/AvailableLocationsSelect";
import PartnerStateSelector from "../Partners/PartnerStateSelector";
import PartnerFilterTable from "../Partners/PartnerFilterTable";
import { coreApi } from "../../api/calls";
import PdfSettingsForm from "../PdfSettingsForm";
import { clearAvailablePartnerStates } from "../../actions";
import usePartnerFiltering from "../../hooks/usePartnerFiltering";
import { getCurrentUserEmployeeId } from "./managerNameOverride";
import ManagerSelect from "../ManagerSelect";

const OfferVacantHousesSentModal = ({
  isVisible,
  onCancel,
  selectedUnits,
  handleApiError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [offerVacantForm] = Form.useForm();
  const defaultManagerEmployeeId = getCurrentUserEmployeeId();

  const {
    selectedPartners,
    filteredPartners,
    checkedPartners,
    handlePartnersLoaded,
    handlePartnerSelectionChange,
    handlePartnerCheck,
    getSelectedPartnerIds,
    selectAll,
    deselectAll,
    resetFiltering,
  } = usePartnerFiltering();

  const productTypeOptions = ["automatic","apartment_buildings", "commercial_spaces"];
  const rewardVisibilityOptions = ["total", "separate", "none"];
  const [rewardVisibility, setRewardVisibility] = useState(rewardVisibilityOptions[0]);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [emailPreview, setEmailPreview] = useState("");
  const [additionalPDF, setAdditionalPDF] = useState("downloadPdf");
  const [productType, setProductType] = useState();
  const [selectedLanguage, setSelectedLanguage] = useState();
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [hideStreetNumber, setHideStreetNumber] = useState(true);
  const [selectedPartnerStates, setSelectedPartnerStates] = useState([]);

  const getLabel = (value) => {
    switch (value) {
      case "total": return t("buildingSetupChanges.createPdfWithTotalReward");
      case "separate": return t("buildingSetupChanges.createPdfWithSeparateReward");
      case "none": return t("buildingSetupChanges.createPdfWithoutReward");
      case "automatic": return t("buildingSetupChanges.automaticSelection");
      case "apartment_buildings": return t("buildingSetupChanges.offerForApartmentBuildings");
      case "commercial_spaces": return t("buildingSetupChanges.offerForCommercialSpaces");
      default: return t(`partners.productTypes.${value}`);
    }
  };

  useEffect(() => {
    if (!selectedUnits || selectedUnits.length === 0) return;

    const firstLanguage = selectedUnits[0].country_branch.country_code_iso_3166;
    const allSame = selectedUnits.every(
      (unit) => unit.country_branch.country_code_iso_3166 === firstLanguage
    );

    if (allSame) {
      if (firstLanguage === 'CZ') {
        setSelectedLanguage("cs");
      } else if (firstLanguage === 'SK') {
        setSelectedLanguage(firstLanguage.toLowerCase());
      } else {
        setSelectedLanguage("en");
      }
    } else {
      setSelectedLanguage("automatic");
    }

    const firstType = selectedUnits[0].partner ? JSON.parse(selectedUnits[0].partner?.product_type) : null;

    const allTypesSame = selectedUnits.every((unit) => {
      const currentType = unit.partner ? JSON.parse(unit.partner?.product_type) : null;
      return (
        Array.isArray(currentType) &&
        Array.isArray(firstType) &&
        currentType.length === firstType.length &&
        currentType.every((val, index) => val === firstType[index])
      );
    });

    if (allTypesSame) {
      setProductType("apartment_buildings".includes(firstType[0]) ? "apartment_buildings" : "commercial_spaces");
    } else {
      setProductType("automatic");
    }
  }, [selectedUnits]);

  useEffect(() => {
    if (!isVisible) return;

    const cityNames = [...new Set(
      (selectedUnits || [])
        .map((unit) => unit?.city_branch?.name || unit?.city_branch?.name_eng)
        .filter(Boolean)
    )].join(", ");

    const countryCode = (
      selectedUnits?.[0]?.country_branch?.country_code_iso_3166 ||
      selectedUnits?.[0]?.city_branch?.country_code_iso_3166 ||
      "CZ"
    ).toUpperCase();

    const cityNameSegment = cityNames ? ` ${cityNames}` : "";
    const subjectTemplates = {
      SK: t("emails.offerSubjectTemplates.offerVacantHousesMassSent.sk", { cityNameSegment }),
      CZ: t("emails.offerSubjectTemplates.offerVacantHousesMassSent.cz", { cityNameSegment }),
      default: t("emails.offerSubjectTemplates.offerVacantHousesMassSent.default", { cityNameSegment }),
    };
    const defaultSubject = subjectTemplates[countryCode] || subjectTemplates.default;

    offerVacantForm.setFieldsValue({
      subject: defaultSubject,
      manager_employee_id: defaultManagerEmployeeId,
    });
  }, [defaultManagerEmployeeId, isVisible, selectedUnits, offerVacantForm, t]);

  const handlePartnersLoadedWithForm = useCallback((partners) => {
    const partnerIds = handlePartnersLoaded(partners);
    if (partnerIds.length > 0) {
      offerVacantForm.setFieldValue("partners", partnerIds);
    }
  }, [handlePartnersLoaded, offerVacantForm]);

  const buildPayload = (values = {}) => {
    const building_units = selectedUnits.map((unit) => {
      return {
        id: unit.id,
        building_unit_id: unit.building_unit?.id || unit.building_unit_id,
        city_branch: unit.city_branch,
        address: `${unit.building_unit?.street || ""}${!hideStreetNumber && unit.building_unit?.house_number
          ? ` ${unit.building_unit.house_number}`
          : ""
        }`,
      };
    });

    const selectedPartnerIds = getSelectedPartnerIds();

    return {
      partner_id: selectedUnits?.[0]?.partner?.id ?? selectedPartnerIds[0],
      new_partner_ids: selectedPartnerIds,
      building_units,
      subject: values?.subject?.trim(),
      manager_employee_id: values?.manager_employee_id || null,
      productType: productType,
      language: selectedLanguage,
      locations: selectedLocations,
      partnerStates: selectedPartnerStates,
      displayPrice: rewardVisibility,
      isMassSend: true,
      hideStreetNumber: hideStreetNumber,
      preserveSelectedUnitsOnly: true,
      additionalPDF: additionalPDF,
    };
  };

  const handleShowSummary = async () => {
    const selectedPartnerIds = getSelectedPartnerIds();

    if (selectedPartnerIds.length === 0) {
      notification.warning({ message: t("partners.validation.selectPartner") });
      return;
    }

    try {
      setIsSubmitting(true);
      const values = await offerVacantForm.validateFields([
        "subject",
        "manager_employee_id",
      ]);
      const payload = buildPayload(values);

      const { data } = await coreApi.post("/partners/offer-vacant-houses-sent-email/preview", payload);
      setEmailPreview(data?.html || t("emails.mailError"));
      setIsButtonDisabled(false);
    } catch (error) {
      if (!error?.errorFields) {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) {
          clearInterval(interval);
          return 99;
        }
        return p + 1;
      });
    }, 100);

        try {
      setIsSubmitting(true);
      
      const values = await offerVacantForm.validateFields();
      const payload = buildPayload(values);

      await coreApi.post("/partners/offer-vacant-houses-sent-email/send", payload);
      notification.success({ message: t("emails.mailSent") });
      handleClose();
    } catch (error) {
      if (!error?.errorFields) {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
      clearInterval(interval);
      setProgress(100);
    }
  };

  const handleClose = () => {
    setEmailPreview("");
    setIsButtonDisabled(true);
    setProductType("automatic");
    setSelectedLanguage("automatic");
    setSelectedLocations([]);
    setHideStreetNumber(true);
    setAdditionalPDF("downloadPdf");
    setSelectedPartnerStates([]);
    resetFiltering();
    offerVacantForm.resetFields();
    dispatch(clearAvailablePartnerStates());
    onCancel();
  };

  return (
    <Modal
      open={isVisible}
      onCancel={handleClose}
      width={1000}
      title={
        <>
          <strong>{t("schedules.informPartnerOptions.offerVacantHouses")}</strong>
        </>
      }
      footer={
        <>
          <Button
            key="confirm"
            type="primary"
            icon={
              isSubmitting ? (
                <Progress
                  type="circle"
                  percent={progress}
                  width={24}
                  strokeWidth={10}
                  showInfo={true}
                  style={{ marginRight: 8 }}
                  status="active"
                />
              ) : (
                <SendOutlined />
              )
            }
            disabled={isButtonDisabled || isSubmitting}
            onClick={handleSubmit}
          >
            {t("common.confirm")}
          </Button>
        </>
      }
    >
      <Form form={offerVacantForm} layout="vertical">
        <PdfSettingsForm
          key="offer-vacant-houses-pdf-settings"
          productType={productType}
          setProductType={setProductType}
          rewardVisibility={rewardVisibility}
          setRewardVisibility={setRewardVisibility}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          showAutomaticLanguage={true}
          showAutomaticProductType={true}
          customRewardVisibilityOptions={["total", "separate", "none"]}
          hideStreetNumber={hideStreetNumber}
          setHideStreetNumber={setHideStreetNumber}
          showStreetNumberSection={true}
          additionalPDF={additionalPDF}
          setAdditionalPDF={setAdditionalPDF}
          showAdditionalPDFSection={true}
        />
        <Form.Item
          label={t("emails.subject")}
          name="subject"
          rules={[{ required: true, whitespace: true, message: t("common.validation.required") }]}
          style={{ marginTop: 16 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("emails.managerNameOverride")}
          name="manager_employee_id"
        >
          <ManagerSelect
            withFormItem={false}
            clearable
            showSearch
            activeOnly
            placeholder={t("emails.managerNameOverride")}
          />
        </Form.Item>
        <Divider />

        <Row gutter={12}>
          <Col span={24}>
            <AvailableLocationsSelect
              label={t("common.location")}
              name="locations"
              mode="multiple"
              onChange={(locations) => {
                dispatch(clearAvailablePartnerStates());
                setSelectedPartnerStates([]);
                
                offerVacantForm.setFieldValue("state", undefined);
                
                if (Array.isArray(locations)) {
                  setSelectedLocations(locations.map(loc => loc.id));
                } else {
                  setSelectedLocations(locations ? [locations.id] : []);
                }
              }}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <PartnerStateSelector
              hideLabel={false}
              clearable={true}
              mode="multiple"
              autoPopulate={true}
              form={offerVacantForm}
              onAutoPopulate={setSelectedPartnerStates}
              sendOnchange={setSelectedPartnerStates}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <AllPartnersForOrdersSelect
              label={t("breadcrumbs.partners")}
              name="partners"
              required={true}
              selectedLocations={selectedLocations}
              selectedPartnerStates={selectedPartnerStates}
              onPartnersLoaded={handlePartnersLoadedWithForm}
              onChange={(partners) => {
                if (Array.isArray(partners)) {
                  handlePartnerSelectionChange(partners);
                } else {
                  handlePartnerSelectionChange(partners ? [partners] : []);
                }
              }}
              value={selectedPartners}
            />
          </Col>
        </Row>

        {filteredPartners.length > 0 && (
          <>
            <Divider />
            <Row gutter={12}>
              <Col span={24}>
                <PartnerFilterTable
                  partners={filteredPartners}
                  checkedPartners={checkedPartners}
                  onPartnerCheck={handlePartnerCheck}
                />
              </Col>
            </Row>
          </>
        )}

        <Row style={{ marginTop: 16 }}>
          <Col>
            <Button
              type="primary"
              onClick={selectAll}
              disabled={isSubmitting || filteredPartners.length === 0}
              style={{marginRight: '20px'}}
            >
              {t("common.selectAll")}
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={deselectAll}
              disabled={isSubmitting || filteredPartners.length === 0}
            >
              {t("common.deselectAll")}
            </Button>
          </Col>
          <Col style={{marginLeft: 'auto'}}>
            <Button type="primary" onClick={handleShowSummary} icon={<EyeOutlined />} disabled={isSubmitting || !selectedPartners.length}>
              {t("common.continue")}
            </Button>
          </Col>
        </Row>
        {emailPreview && (
          <Card style={{ marginTop: 24 }}>
            <iframe
              title="Email Preview"
              style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
              srcDoc={emailPreview}
            />
          </Card>
        )}
      </Form>
    </Modal>
  );
};

export default OfferVacantHousesSentModal;
